'use client';

import type { KeyboardEvent, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Button } from '../../../../../components/styled/button';
import { Text } from '../../../../../components/styled/text';

type PendingObjectReviewCardProps = {
  title?: string;
  fallbackTitle: string;
  statusMessage?: string;
  portalVisibilityMessage?: string;
  actionLabel?: string;
  statusIcon?: ReactNode;
  statusIconColor?: string;
  icon: ReactNode;
  onReviewAndSave?: () => void;
  disabled?: boolean;
  iconColor?: string;
  iconBorderColor?: string;
  iconBg?: string;
  onClick?: () => void;
  trailingAction?: ReactNode;
};

export function PendingObjectReviewCard({
  title,
  fallbackTitle,
  statusMessage,
  portalVisibilityMessage,
  actionLabel,
  statusIcon,
  statusIconColor = 'fg.neutral.success',
  icon,
  onReviewAndSave,
  disabled,
  iconColor = 'colorPalette.default',
  iconBorderColor = 'border.default',
  iconBg = 'bg.default',
  onClick,
  trailingAction,
}: PendingObjectReviewCardProps) {
  const t = useTranslations(
    'designSystem.chat.hitlRequestConfirmationPromptCard',
  );
  const displayTitle = title ?? fallbackTitle;
  const canOpen = Boolean(onClick && !disabled);
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    onClick?.();
  };

  return (
    <VStack
      gap={2.5}
      width="full"
      alignItems="stretch"
      cursor={canOpen ? 'pointer' : 'default'}
      onClick={canOpen ? onClick : undefined}
      onKeyDown={canOpen ? handleKeyDown : undefined}
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      aria-label={canOpen ? displayTitle : undefined}
    >
      {statusMessage ? (
        <HStack gap={1.5} alignItems="center">
          {statusIcon ? (
            <Box color={statusIconColor} display="flex" alignItems="center">
              {statusIcon}
            </Box>
          ) : null}
          <HStack gap={1} alignItems="center" minWidth={0}>
            <Text fontSize="xs" color="fg.muted">
              {statusMessage}
            </Text>
            {actionLabel ? (
              <Text fontSize="xs" color="fg.muted">
                {actionLabel}
              </Text>
            ) : null}
          </HStack>
        </HStack>
      ) : null}

      <HStack
        gap={2}
        alignItems="center"
        justifyContent="space-between"
        width="full"
        borderWidth="1px"
        borderColor="border.neutral.subtle"
        bg="bg.default"
        borderRadius="lg"
        px={3}
        py={2.5}
      >
        <HStack gap={2} alignItems="center" minWidth={0}>
          <Box
            width={10}
            height={10}
            borderRadius="md"
            borderWidth="1px"
            borderColor={iconBorderColor}
            color={iconColor}
            bg={iconBg}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            {icon}
          </Box>
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color="fg.default"
            truncate
            minWidth={0}
          >
            {displayTitle}
          </Text>
        </HStack>

        {onReviewAndSave ? (
          <Button
            variant="solid"
            colorPalette="terra"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              onReviewAndSave();
            }}
            disabled={disabled}
            flexShrink={0}
          >
            {t('review')}
          </Button>
        ) : trailingAction ? (
          trailingAction
        ) : null}
      </HStack>

      {portalVisibilityMessage ? (
        <Text fontSize="xs" color="fg.subtle" textAlign="center">
          {portalVisibilityMessage}
        </Text>
      ) : null}
    </VStack>
  );
}
